import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { authClient } from "@/lib/auth";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { error } = await authClient.requestPasswordReset({ email, redirectTo: "/reset-password" });
      if (error) setErr(error.message ?? "Something went wrong");
      else setSent(true);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={sent ? "Check your email" : "Reset your password"} eyebrow="Members">
      {sent ? (
        <p
          className="text-center text-sm"
          style={{ fontFamily: fonts.body, color: `${colors.navy}aa`, lineHeight: 1.6 }}
        >
          If an account exists for <strong style={{ color: colors.navy }}>{email}</strong>, we&rsquo;ve
          emailed a link to reset your password. The link expires in one hour.
        </p>
      ) : (
        <form onSubmit={submit} className="grid gap-4">
          <p
            className="text-center text-sm"
            style={{ fontFamily: fonts.body, color: `${colors.navy}aa`, lineHeight: 1.55, marginTop: -8 }}
          >
            Enter your email and we&rsquo;ll send a reset link.
          </p>
          <TextInput
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {err && (
            <p
              className="text-center text-sm"
              style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
            >
              {err}
            </p>
          )}
          <Button type="submit" className="mt-1 w-full" disabled={busy}>
            {busy ? "…" : "Send reset link"}
          </Button>
        </form>
      )}

      <div className="mt-5 text-center">
        <Link
          to="/login"
          className="text-sm underline transition-opacity hover:opacity-70"
          style={{ fontFamily: fonts.body, fontWeight: 600, color: colors.berryRose, letterSpacing: "0.03em" }}
        >
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}

// Shared brand auth card layout (mirrors Login.tsx).
export function AuthShell({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative flex min-h-dvh items-center justify-center overflow-hidden px-6 py-16"
      style={{ backgroundColor: colors.cream }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-16 -top-24 h-72 w-72 rounded-full"
        style={{ background: colors.berryPink, opacity: 0.18, filter: "blur(64px)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-12 h-80 w-80 rounded-full"
        style={{ background: colors.lavender, opacity: 0.18, filter: "blur(64px)" }}
      />
      <div className="relative z-10 w-full max-w-sm">
        <Link to="/" className="mb-6 flex flex-col items-center gap-2 no-underline">
          <img
            src="/logos/text-logo-purple.png"
            alt="A Few Loose Threads"
            className="h-10 w-auto"
            width={500}
            height={185}
          />
        </Link>
        <div
          className="rounded-[20px] border p-8 sm:p-10"
          style={{
            backgroundColor: "white",
            borderColor: `${colors.tan}26`,
            boxShadow: "0 20px 50px -28px rgba(8,45,61,0.35)",
          }}
        >
          <p
            className="mb-2 text-center text-xs uppercase"
            style={{ fontFamily: fonts.body, fontWeight: 700, letterSpacing: "0.2em", color: colors.berryRose }}
          >
            {eyebrow}
          </p>
          <h1
            className="mb-7 text-center text-3xl"
            style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy }}
          >
            {title}
          </h1>
          {children}
        </div>
      </div>
    </div>
  );
}
